import {
  inject,
  provideAppInitializer,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import type { CanActivateFn } from '@angular/router';

import {
  AUTH_GUARD,
  AuthenticationAuroraAdapterService,
  authenticationLocalGuard,
  AuthenticationService,
  authorizationCodeGuard,
  CurrentAccountService,
  provideAuthenticatedInitializer,
  provideAuthentication,
} from '@aurora/modules/authentication';
import { provideApollo } from '@aurora/modules/graphql';
import { LangService } from '@aurora/modules/lang';
import { ManualService } from '@aurora/modules/manual';
import { provideValidationMessages } from '@aurora/pipes';
import { environment } from 'environments/environment';
import { AUTH_STRATEGY, type AuthStrategy } from '../environments/environment.type';
import { auroraPortProviders } from './aurora-port.providers';
import { auroraWidgetProviders } from './aurora-widget.providers';
/**
 * Returns the correct authentication guard for the given deploy-time strategy.
 *
 * Exported so that unit tests can exercise the REAL selection logic:
 *   - AUTH_STRATEGY.LOCAL               → authenticationLocalGuard
 *   - AUTH_STRATEGY.AUTHORIZATION_CODE  → authorizationCodeGuard
 *
 * Inverting this condition or renaming a branch will immediately fail tests
 * that import and call this function.
 */
export const authGuardForStrategy = (strategy: AuthStrategy): CanActivateFn => {
  if (strategy === AUTH_STRATEGY.AUTHORIZATION_CODE) {
    return authorizationCodeGuard;
  }

  return authenticationLocalGuard;
};

export const provideAurora = (): Array<Provider | EnvironmentProviders> => [
  provideAuthentication(),
  ...provideApollo(),
  { provide: AuthenticationService, useClass: AuthenticationAuroraAdapterService },
  // The framework ports bound to an adapter (`CurrentAccountService`) live in `aurora-port.providers.ts`.
  ...auroraPortProviders,
  // Register every BC's Aurora widgets into the widget multi-token with one spread. The per-BC
  // `provideWidget(...)` arrays are aggregated in `aurora-widget.providers.ts`, so adding a BC's
  // widgets never touches this composition root. Consumers read them via
  // `inject(AURORA_WIDGET, { optional: true })`.
  ...auroraWidgetProviders,
  // Load the current account once the session is established (sign-in or already-valid session on
  // load) — see provideAuthenticatedInitializer. The sidebar just reads the resulting signals.
  provideAuthenticatedInitializer(() => inject(CurrentAccountService).load()),
  // The in-app manual index (current language) loads once authenticated and reloads on language change.
  provideAuthenticatedInitializer(() => inject(ManualService).start()),
  // AUTH_GUARD is wired from environment.authStrategy via authGuardForStrategy:
  //   AUTH_STRATEGY.LOCAL               → authenticationLocalGuard (authorization code + PKCE)
  //   AUTH_STRATEGY.AUTHORIZATION_CODE  → authorizationCodeGuard (delegates to hub, no local form)
  { provide: AUTH_GUARD, useValue: authGuardForStrategy(environment.authStrategy) },
  provideValidationMessages({
    uniqueEmail: { key: 'Validations.UniqueEmail' },
    uniqueUsername: { key: 'Validations.UniqueUsername' },
    matchOther: { key: 'Validations.MatchOther' },
    passwordRule: { key: 'Validations.PasswordRule' },
    absoluteHttpUrl: { key: 'Validations.AbsoluteHttpUrl' },
  }),
  // Non-blocking: kick off lang load at boot. The signal hydrates synchronously
  // from SessionService localStorage; this just refreshes/warms when stale.
  // If the request fails, the app still boots and uses the cached or empty state.
  provideAppInitializer(() => {
    inject(LangService)
      .load()
      .catch((err) => {
        console.error('[LangService] initial load failed, using cached/empty state', err);
      });
  }),
];
